import { useState } from "react";
import { withKey, stripKey } from "../lib/listKeys";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import {
  Brain, Plus, Trash2, Save, MessageSquare, PhoneCall, Mail, Trophy, Users, Send, ShieldCheck,
} from "lucide-react";

const CHANNELS = {
  sms: { label: "SMS", icon: MessageSquare, color: "text-sky-400", ring: "border-sky-400/30" },
  call: { label: "Call", icon: PhoneCall, color: "text-amber-400", ring: "border-amber-400/30" },
  email: { label: "Email", icon: Mail, color: "text-emerald-400", ring: "border-emerald-400/30" },
};
const GOALS = [
  { v: "any", label: "Reply or pipeline move" },
  { v: "reply", label: "Lead replies" },
  { v: "qualified", label: "Becomes qualified" },
  { v: "won", label: "Closed won" },
];
const clone = (x) => JSON.parse(JSON.stringify(x));
const newVariant = (label) => ({ id: null, label, subject: "", message: "" });

function EnrollDialog({ campaign, open, onOpenChange }) {
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState({});
  const { data: leads = [] } = useQuery({
    queryKey: ["enroll-leads", search],
    queryFn: () => api.get("/leads", { params: search ? { q: search } : {} }).then((r) => r.data),
    enabled: open,
  });
  const ids = Object.keys(selected).filter((k) => selected[k]);
  const enroll = async () => {
    try {
      const { data } = await api.post(`/campaigns/${campaign.id}/enroll`, { lead_ids: ids });
      toast.success(`Enrolled ${data.enrolled} lead(s)${data.skipped ? `, skipped ${data.skipped}` : ""}`);
      setSelected({});
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Enroll failed");
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border max-w-lg" data-testid="enroll-dialog">
        <DialogHeader><DialogTitle>Enroll leads → {campaign.name}</DialogTitle></DialogHeader>
        <Input placeholder="Search by name, company, phone…" value={search}
          onChange={(e) => setSearch(e.target.value)} data-testid="enroll-search" />
        <div className="max-h-72 overflow-y-auto space-y-1 mt-1 -mx-1 px-1">
          {leads.map((l) => (
            <label key={l.id} data-testid={`enroll-lead-${l.id}`}
              className="flex items-center gap-3 rounded-lg px-3 py-2 hover:bg-secondary cursor-pointer">
              <Checkbox checked={!!selected[l.id]}
                onCheckedChange={(v) => setSelected((s) => ({ ...s, [l.id]: v }))} />
              <div className="min-w-0">
                <p className="text-sm font-medium truncate">{l.name || l.company || "Unnamed"}</p>
                <p className="text-xs text-muted-foreground truncate">
                  {[l.company, l.phone || "no phone", l.source].filter(Boolean).join(" · ")}
                </p>
              </div>
            </label>
          ))}
          {leads.length === 0 && <p className="text-sm text-muted-foreground py-6 text-center">No leads found.</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={enroll} disabled={!ids.length} data-testid="enroll-submit"
            className="bg-primary text-primary-foreground hover:bg-primary/90">
            Enroll {ids.length || ""}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function VariantBlock({ campId, step, i, v, j, updateVariant, removeVariant, canRemove }) {
  return (
    <div data-testid={`variant-${campId}-${i}-${j}`}
      className={`rounded-xl border p-3 space-y-2 ${v.winner ? "border-amber-400/50 bg-amber-400/5" : "border-border bg-background/40"}`}>
      <div className="flex items-center gap-2">
        <Input value={v.label} onChange={(e) => updateVariant(i, j, { label: e.target.value })}
          className="h-7 w-32 text-xs font-semibold border-0 px-2 bg-secondary" data-testid={`variant-label-${campId}-${i}-${j}`} />
        <div className="flex-1" />
        <span className="text-[11px] text-muted-foreground tabular-nums" data-testid={`variant-stats-${campId}-${i}-${j}`}>
          {v.sends || 0} sent · {Math.round((v.rate || 0) * 100)}% conv
        </span>
        {v.winner && (
          <Badge className="bg-amber-400/15 text-amber-400 border-amber-400/30 gap-1" data-testid={`variant-winner-${campId}-${i}-${j}`}>
            <Trophy className="h-3 w-3" /> Winner
          </Badge>
        )}
        {canRemove && (
          <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => removeVariant(i, j)}
            data-testid={`remove-variant-${campId}-${i}-${j}`}>
            <Trash2 className="h-3.5 w-3.5 text-rose-400" />
          </Button>
        )}
      </div>
      {step.channel === "email" && (
        <Input value={v.subject} onChange={(e) => updateVariant(i, j, { subject: e.target.value })}
          placeholder="Email subject… use {name}" className="h-8 text-sm" data-testid={`variant-subject-${campId}-${i}-${j}`} />
      )}
      {step.channel === "call" && step.call_mode === "ai" ? (
        <p className="text-xs text-muted-foreground italic px-1" data-testid={`variant-ai-hint-${campId}-${i}-${j}`}>Uses your AI Call Agent script & ElevenLabs voice.</p>
      ) : (
        <Textarea value={v.message} onChange={(e) => updateVariant(i, j, { message: e.target.value })}
          rows={step.channel === "email" ? 4 : 2}
          placeholder={step.channel === "call" ? "Note for the rep… use {name}" : "Message… use {name}"}
          data-testid={`variant-message-${campId}-${i}-${j}`} />
      )}
    </div>
  );
}

function CampaignCard({ camp, onChanged }) {
  const [name, setName] = useState(camp.name);
  const [goal, setGoal] = useState(camp.goal || "any");
  const [active, setActive] = useState(camp.active);
  const [steps, setSteps] = useState(() => clone(camp.steps || []).map(withKey));
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [enrollOpen, setEnrollOpen] = useState(false);

  const touch = () => setDirty(true);
  const updateStep = (i, patch) => { setSteps((s) => s.map((st, idx) => (idx === i ? { ...st, ...patch } : st))); touch(); };
  const updateVariant = (i, j, patch) => { setSteps((s) => s.map((st, idx) => (idx === i ? { ...st, variants: st.variants.map((v, k) => (k === j ? { ...v, ...patch } : v)) } : st))); touch(); };
  const addVariant = (i) => { setSteps((s) => s.map((st, idx) => (idx === i ? { ...st, variants: [...st.variants, newVariant(String.fromCharCode(65 + st.variants.length))] } : st))); touch(); };
  const removeVariant = (i, j) => { setSteps((s) => s.map((st, idx) => (idx === i ? { ...st, variants: st.variants.filter((_, k) => k !== j) } : st))); touch(); };
  const addStep = () => { setSteps((s) => [...s, withKey({ channel: "sms", delay_hours: 24, call_mode: "ai", variants: [newVariant("A")] })]); touch(); };
  const removeStep = (i) => { setSteps((s) => s.filter((_, idx) => idx !== i)); touch(); };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/campaigns/${camp.id}`, { name, goal, active, steps: steps.map(stripKey) });
      toast.success("Campaign saved");
      setDirty(false);
      onChanged();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };
  const remove = async () => {
    if (!window.confirm("Delete this campaign? Active enrollments will stop.")) return;
    try {
      await api.delete(`/campaigns/${camp.id}`);
      toast.success("Campaign deleted");
      onChanged();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not delete campaign");
    }
  };
  const toggleActive = async (v) => {
    setActive(v);
    try { await api.put(`/campaigns/${camp.id}`, { active: v }); onChanged(); }
    catch { toast.error("Failed to update"); }
  };

  return (
    <div className="rounded-2xl border border-border bg-card p-6" data-testid={`campaign-${camp.id}`}>
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary shrink-0">
          <Brain className="h-5 w-5" />
        </div>
        <Input value={name} onChange={(e) => { setName(e.target.value); touch(); }}
          className="font-display font-bold border-0 px-0 h-auto text-lg focus-visible:ring-0 bg-transparent w-auto min-w-[12rem] flex-1"
          data-testid={`campaign-name-${camp.id}`} />
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">Goal</span>
          <Select value={goal} onValueChange={(v) => { setGoal(v); touch(); }}>
            <SelectTrigger className="h-8 w-52" data-testid={`campaign-goal-${camp.id}`}><SelectValue /></SelectTrigger>
            <SelectContent className="bg-popover border-border">
              {GOALS.map((g) => <SelectItem key={g.v} value={g.v}>{g.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="text-xs text-muted-foreground">{active ? "Active" : "Paused"}</span>
          <Switch checked={active} onCheckedChange={toggleActive} data-testid={`campaign-active-${camp.id}`} />
        </div>
        <Button size="icon" variant="ghost" onClick={remove} data-testid={`delete-campaign-${camp.id}`}>
          <Trash2 className="h-4 w-4 text-rose-400" />
        </Button>
      </div>

      <div className="flex flex-wrap gap-4 mt-3 text-xs text-muted-foreground">
        <span data-testid={`campaign-active-count-${camp.id}`}><b className="text-foreground">{camp.active_enrollments || 0}</b> in progress</span>
        <span data-testid={`campaign-converted-${camp.id}`}><b className="text-emerald-400">{camp.converted_count || 0}</b> converted</span>
        <span><b className="text-foreground">{camp.total_enrollments || 0}</b> total enrolled</span>
      </div>

      <div className="mt-5 space-y-3">
        {steps.map((step, i) => {
          const ch = CHANNELS[step.channel] || CHANNELS.sms;
          const Icon = ch.icon;
          return (
            <div key={step._k} className={`rounded-xl border ${ch.ring} bg-background/40 p-4`} data-testid={`step-${camp.id}-${i}`}>
              <div className="flex flex-wrap items-center gap-2 mb-3">
                <div className={`flex h-7 w-7 items-center justify-center rounded-lg bg-secondary ${ch.color}`}>
                  <Icon className="h-4 w-4" />
                </div>
                <span className="text-xs font-semibold text-muted-foreground mr-1">Step {i + 1}</span>
                <Select value={step.channel} onValueChange={(v) => updateStep(i, { channel: v })}>
                  <SelectTrigger className="h-8 w-28" data-testid={`step-channel-${camp.id}-${i}`}><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-popover border-border">
                    <SelectItem value="sms">SMS</SelectItem>
                    <SelectItem value="call">Call</SelectItem>
                    <SelectItem value="email">Email</SelectItem>
                  </SelectContent>
                </Select>
                {step.channel === "call" && (
                  <Select value={step.call_mode || "ai"} onValueChange={(v) => updateStep(i, { call_mode: v })}>
                    <SelectTrigger className="h-8 w-36" data-testid={`step-callmode-${camp.id}-${i}`}><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-popover border-border">
                      <SelectItem value="ai">AI Agent call</SelectItem>
                      <SelectItem value="task">Human rep task</SelectItem>
                    </SelectContent>
                  </Select>
                )}
                <div className="flex items-center gap-1.5 ml-auto">
                  <span className="text-xs text-muted-foreground">wait</span>
                  <Input type="number" min="0" value={step.delay_hours}
                    onChange={(e) => updateStep(i, { delay_hours: parseFloat(e.target.value) || 0 })}
                    className="h-8 w-20" data-testid={`step-delay-${camp.id}-${i}`} />
                  <span className="text-xs text-muted-foreground">hrs</span>
                  <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => removeStep(i)} data-testid={`remove-step-${camp.id}-${i}`}>
                    <Trash2 className="h-3.5 w-3.5 text-rose-400" />
                  </Button>
                </div>
              </div>
              {step.variants.length > 1 && (
                <p className="text-[11px] text-primary font-medium mb-2 flex items-center gap-1">
                  <Brain className="h-3 w-3" /> A/B test — the brain auto-favours the top converter
                </p>
              )}
              <div className="space-y-2">
                {step.variants.map((v, j) => (
                  <VariantBlock key={v.id || j} campId={camp.id} step={step} i={i} v={v} j={j}
                    updateVariant={updateVariant} removeVariant={removeVariant}
                    canRemove={step.variants.length > 1} />
                ))}
              </div>
              {step.channel !== "call" && (
                <Button variant="ghost" size="sm" className="mt-2 text-primary" onClick={() => addVariant(i)} data-testid={`add-variant-${camp.id}-${i}`}>
                  <Plus className="h-3.5 w-3.5 mr-1" /> Add A/B variant
                </Button>
              )}
            </div>
          );
        })}
      </div>

      <div className="flex flex-wrap items-center gap-2 mt-4">
        <Button variant="outline" size="sm" onClick={addStep} data-testid={`add-step-${camp.id}`}>
          <Plus className="h-4 w-4 mr-1.5" /> Add step
        </Button>
        <Button variant="outline" size="sm" onClick={() => setEnrollOpen(true)} data-testid={`enroll-campaign-${camp.id}`}>
          <Users className="h-4 w-4 mr-1.5" /> Enroll leads
        </Button>
        <div className="flex-1" />
        {dirty && <span className="text-xs text-amber-400">Unsaved changes</span>}
        <Button size="sm" onClick={save} disabled={saving} data-testid={`save-campaign-${camp.id}`}
          className="bg-primary text-primary-foreground hover:bg-primary/90">
          <Save className="h-4 w-4 mr-1.5" /> {saving ? "Saving…" : "Save"}
        </Button>
      </div>

      <EnrollDialog campaign={camp} open={enrollOpen} onOpenChange={setEnrollOpen} />
    </div>
  );
}

function InsightsPanel() {
  const { data } = useQuery({
    queryKey: ["insights"],
    queryFn: () => api.get("/orchestrator/insights").then((r) => r.data),
  });
  const emailDigest = async () => {
    try {
      const { data: d } = await api.post("/orchestrator/insights/email");
      if (d.sent) toast.success("Digest emailed to you");
      else toast.message("Not enough A/B data to send yet");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not email digest");
    }
  };
  const withData = (data?.campaigns || []).filter((c) => c.steps.length);
  return (
    <div className="rounded-2xl border border-border bg-card p-6" data-testid="insights-panel">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-1">
        <h2 className="font-display text-lg font-bold flex items-center gap-2">
          <Trophy className="h-5 w-5 text-amber-400" /> Winning Scripts
        </h2>
        <Button variant="outline" size="sm" onClick={emailDigest} data-testid="email-digest-button">
          <Mail className="h-4 w-4 mr-1.5" /> Email me the digest
        </Button>
      </div>
      <p className="text-sm text-muted-foreground mb-4">Which scripts convert best across your A/B steps — a weekly digest also lands in your inbox.</p>
      {withData.length === 0 ? (
        <p className="text-sm text-muted-foreground" data-testid="insights-empty">
          Not enough A/B data yet — the brain needs a few sends per variant before it can name winners.
        </p>
      ) : (
        <div className="space-y-4">
          {withData.map((c) => (
            <div key={c.id} className="rounded-xl border border-border bg-background/40 p-4" data-testid={`insights-campaign-${c.id}`}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="font-semibold text-sm">{c.name}</p>
                <span className="text-xs text-muted-foreground">{c.converted}/{c.total_enrolled} converted · {Math.round(c.conversion_rate * 100)}%</span>
              </div>
              {c.steps.map((s) => (
                <div key={s.index} className="flex flex-wrap items-center gap-2 mt-2 text-xs">
                  <Badge variant="outline" className="shrink-0">{(s.channel || "").toUpperCase()} · Step {s.index + 1}</Badge>
                  {s.leader ? (
                    <span className={`flex items-center gap-1 ${s.leader.confident ? "text-amber-400 font-medium" : "text-muted-foreground"}`}>
                      {s.leader.confident && <Trophy className="h-3 w-3" />}
                      &ldquo;{s.leader.label}&rdquo; leads at {Math.round(s.leader.rate * 100)}%{s.leader.confident ? "" : " (needs more data)"}
                    </span>
                  ) : <span className="text-muted-foreground">no sends yet</span>}
                  <span className="text-muted-foreground ml-auto">
                    {s.variants.map((v) => `${v.label} ${Math.round(v.rate * 100)}% (${v.sends})`).join("  ·  ")}
                  </span>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Orchestrator() {
  const qc = useQueryClient();
  const [newName, setNewName] = useState("");
  const { data: campaigns = [] } = useQuery({
    queryKey: ["campaigns"],
    queryFn: () => api.get("/campaigns").then((r) => r.data),
  });
  const refresh = () => qc.invalidateQueries({ queryKey: ["campaigns"] });

  const create = async () => {
    if (!newName.trim()) return;
    try {
      await api.post("/campaigns", {
        name: newName, goal: "any",
        steps: [{ channel: "sms", delay_hours: 0, call_mode: "ai", variants: [{ label: "A", subject: "", message: "Hi {name}! " }] }],
      });
      setNewName("");
      refresh();
      toast.success("Campaign created");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not create campaign");
    }
  };
  const testEmail = async () => {
    try {
      const { data } = await api.post("/orchestrator/test-email");
      toast.success(`Email channel works ✓${data.email_id ? ` (${String(data.email_id).slice(0, 8)})` : ""}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Email test failed");
    }
  };

  return (
    <div data-testid="orchestrator-page" className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight flex items-center gap-2">
            <Brain className="h-7 w-7 text-primary" /> AI Orchestrator
          </h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            Multi-channel follow-up brain. Run leads through SMS → call → email, A/B-test your scripts,
            and let it auto-shift traffic to whatever converts best.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
          <Button variant="outline" size="sm" onClick={testEmail} data-testid="test-email-button">
            <Send className="h-4 w-4 mr-1.5" /> Test email
          </Button>
          <Input value={newName} onChange={(e) => setNewName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && create()}
            placeholder="New campaign name" className="flex-1 min-w-0 sm:w-52 sm:flex-none" data-testid="new-campaign-input" />
          <Button onClick={create} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="create-campaign-button">
            <Plus className="h-4 w-4 mr-1.5" /> Create
          </Button>
        </div>
      </div>

      <div className="rounded-xl border border-emerald-400/20 bg-emerald-400/5 px-4 py-3 flex items-start gap-2.5" data-testid="email-compliance-note">
        <ShieldCheck className="h-4 w-4 text-emerald-400 mt-0.5 shrink-0" />
        <p className="text-xs text-muted-foreground">
          <b className="text-foreground">Email compliance (CASL):</b> email steps send to leads who've engaged (replied,
          opted-in, or are customers) and to businesses whose email is published on their own website (implied consent).
          Every email includes your sender name, mailing address and a one-click unsubscribe — set these in ☰ → Email settings.
        </p>
      </div>

      <InsightsPanel />

      <div className="space-y-5">
        {campaigns.map((c) => <CampaignCard key={c.id} camp={c} onChanged={refresh} />)}
        {campaigns.length === 0 && (
          <div className="rounded-2xl border border-dashed border-border p-12 text-center text-muted-foreground" data-testid="orchestrator-empty">
            No campaigns yet. Create one above to start orchestrating multi-channel follow-ups.
          </div>
        )}
      </div>
    </div>
  );
}
